/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Layout } from './components/Layout';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { isFirebaseConfigured } from './firebase';
import { FirebaseSetupWarning } from './components/FirebaseSetupWarning';
import { ProtectedRoute } from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { useState, useEffect, Suspense, lazy } from 'react';
import { lazyWithRetry } from './lib/lazyWithRetry';
import './i18n';

// Lazy load all page components (handling named exports)
const Home = lazyWithRetry(() => import('./pages/Home').then(m => m.Home));
const Directory = lazyWithRetry(() => import('./pages/Directory').then(m => m.Directory));
const QuoteForm = lazyWithRetry(() => import('./pages/QuoteForm').then(m => m.QuoteForm));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard').then(m => m.Dashboard));
const Jobs = lazyWithRetry(() => import('./pages/Jobs').then(m => m.Jobs));
const Pricing = lazyWithRetry(() => import('./components/Pricing').then(m => m.Pricing));
const Marketplace = lazyWithRetry(() => import('./pages/Marketplace').then(m => m.Marketplace));
const ProductDetails = lazyWithRetry(() => import('./pages/ProductDetails').then(m => m.ProductDetails));
const Checkout = lazyWithRetry(() => import('./pages/Checkout').then(m => m.Checkout));
const TrackOrder = lazyWithRetry(() => import('./pages/TrackOrder').then(m => m.TrackOrder));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard').then(m => m.AdminDashboard));
const ProfessionalProfile = lazyWithRetry(() => import('./pages/ProfessionalProfile').then(m => m.ProfessionalProfile));
const Terms = lazyWithRetry(() => import('./pages/Terms').then(m => m.Terms));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy').then(m => m.PrivacyPolicy));
const CookiePolicy = lazyWithRetry(() => import('./pages/CookiePolicy').then(m => m.CookiePolicy));
const LegalNotice = lazyWithRetry(() => import('./pages/LegalNotice').then(m => m.LegalNotice));
const SellerAgreement = lazyWithRetry(() => import('./pages/SellerAgreement').then(m => m.SellerAgreement));
const PaymentPolicy = lazyWithRetry(() => import('./pages/PaymentPolicy').then(m => m.PaymentPolicy));
const AntiFraudPolicy = lazyWithRetry(() => import('./pages/AntiFraudPolicy').then(m => m.AntiFraudPolicy));
const Contact = lazyWithRetry(() => import('./pages/Contact').then(m => m.Contact));
const FAQ = lazyWithRetry(() => import('./pages/FAQ').then(m => m.FAQ));
const Cart = lazyWithRetry(() => import('./pages/Cart').then(m => m.Cart));
const MyAccount = lazyWithRetry(() => import('./pages/MyAccount').then(m => m.MyAccount));
const RatesAndBenefits = lazyWithRetry(() => import('./pages/RatesAndBenefits').then(m => m.RatesAndBenefits));
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding').then(m => m.Onboarding));
const VerifyBadge = lazyWithRetry(() => import('./pages/VerifyBadge').then(m => m.VerifyBadge));
const AuthAction = lazyWithRetry(() => import('./pages/AuthAction').then(m => m.AuthAction));
import { CookieBanner } from './components/CookieBanner';

export default function App() {
  if (!isFirebaseConfigured) {
    return <FirebaseSetupWarning />;
  }

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Cargando...</div>}>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="directory" element={<Directory />} />
                  <Route path="directory/:id" element={<ProfessionalProfile />} />
                  <Route path="quote" element={<QuoteForm />} />
                  <Route path="marketplace" element={<Marketplace />} />
                  <Route path="marketplace/:id" element={<ProductDetails />} />
                  <Route path="cart" element={<Cart />} />
                  <Route path="my-account" element={<MyAccount />} />
                  <Route path="onboarding" element={<Onboarding />} />
                  <Route path="verify/:userId" element={<VerifyBadge />} />
                  <Route path="pricing" element={<Pricing />} />
                  <Route path="checkout" element={<Checkout />} />
                  <Route path="track-order" element={<TrackOrder />} />
                  <Route path="jobs" element={<Jobs />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="admin" element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="terms" element={<Terms />} />
                  <Route path="privacy" element={<PrivacyPolicy />} />
                  <Route path="cookies" element={<CookiePolicy />} />
                  <Route path="legal" element={<LegalNotice />} />
                  <Route path="seller-agreement" element={<SellerAgreement />} />
                  <Route path="payment-policy" element={<PaymentPolicy />} />
                  <Route path="anti-fraud" element={<AntiFraudPolicy />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="faq" element={<FAQ />} />
                  <Route path="rates-and-benefits" element={<RatesAndBenefits />} />
                  <Route path="auth/action" element={<AuthAction />} />
                </Route>
              </Routes>
              <CookieBanner />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </Suspense>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
